import type { MessageLogsListParams, MessageLogsSortKey, MessageLogsMessageLogsFiltersProps } from '../types/pages'
/**
 * SMS Message Logs — React parity with Angular `ReportsMessageLogsComponent`.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronsUpDown, Loader2 } from 'lucide-react'
import { PageShell } from '@/components/layout/PageShell'
import { ListSearchToolbar } from '@/components/ui/ListSearchToolbar'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { ListFilterDrawer } from '@/components/ui/ListFilterDrawer'
import { formatDateForAPI } from '@/features/invitee/utils/inviteeFormatters'
import { ROUTE_BUILDERS } from '@/config/routes'
import { showApiError } from '@/lib/utils/notifications'
import { useUserRole } from '@/hooks/useUserRole'
import { cn } from '@lib/utils/helpers'
import { ReportTableScroll } from '../components/ReportTableScroll'
import reportStyles from '../styles/reportsTable.module.css'
import { ReportDateRangePicker } from '../components/ReportDateRangePicker'
import { SearchableSelect } from '@/components/ui/SearchableSelect'
import { getMessageLogList } from '../services/reportsService'
import type { MessageLogListResponse, MessageLogRow } from '../types/api'
import { ymdToDmy } from '../utils/reportDateParams'
import {
  buildMessageLogsSessionFromParams,
  clearMessageLogsSession,
  readMessageLogsSession,
  sessionFilterToMessageLogsPatch,
  writeMessageLogsSession,
} from '../utils/messageLogsSessionFilter'
import {
  DEFAULT_MESSAGE_LOGS_PARAMS as DEFAULT_PARAMS,
  MESSAGE_LOGS_SORT_COLUMNS as SORT_COLUMNS,
} from '../constants/pages/messageLogsPage'
import {
  LABEL_CLASS,
} from '../constants/configuredReportStyles'

function initialParams(): MessageLogsListParams {
  const session = readMessageLogsSession()
  if (!session) return { ...DEFAULT_PARAMS }
  return { ...DEFAULT_PARAMS, ...sessionFilterToMessageLogsPatch(session) }
}

function buildMessageLogQueryParams(p: MessageLogsListParams): URLSearchParams {
  const qs = new URLSearchParams()
  qs.set('page', String(p.page))
  qs.set('perPage', String(p.perPage))
  qs.set('sortBy', p.sortBy)
  qs.set('orderBy', p.orderBy)
  if (p.q.trim()) qs.set('q', p.q.trim())
  if (p.delivered) qs.set('delivered', p.delivered)
  const fromDt = ymdToDmy(p.fromDt)
  const toDt = ymdToDmy(p.toDt)
  if (fromDt) qs.set('fromDt', fromDt)
  if (toDt) qs.set('toDt', toDt)
  return qs
}

function countActiveFilters(p: MessageLogsListParams): number {
  let count = 0
  if (p.q.trim()) count++
  if (p.delivered) count++
  if (p.fromDt || p.toDt) count++
  return count
}

function formatDisplayDate(value: string | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }
  return String(value)
}

function padApplicantId(value: number | undefined): string {
  if (value == null) return ''
  return String(value).padStart(8, '0')
}


function MessageLogsFilters({
  filters,
  isOpen,
  onClose,
  onApply,
  onReset,
}: MessageLogsMessageLogsFiltersProps) {
  const [draft, setDraft] = useState<MessageLogsListParams>(filters)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: re-sync the filter draft to applied filters each time the drawer opens
    if (isOpen) setDraft(filters)
  }, [isOpen, filters])

  return (
    <ListFilterDrawer
      isOpen={isOpen}
      onClose={onClose}
      onReset={onReset}
      onDone={() => {
        onApply(draft)
        onClose()
      }}
      testIdPrefix="message-logs"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <label className={LABEL_CLASS}>Delivered</label>
          <SearchableSelect
            searchable={false}
            options={[
              { value: '', label: 'All' },
              { value: 'delivered', label: 'Delivered' },
              { value: 'not delivered', label: 'Undelivered' },
            ]}
            value={draft.delivered}
            onChange={(val) => {
              const v = Array.isArray(val) ? val[0] : val
              setDraft((prev) => ({ ...prev, delivered: v || '' }))
            }}
            placeholder="Delivered Status"
          />
        </div>

        <div className="space-y-2">
          <label className={LABEL_CLASS}>Sent Date</label>
          <ReportDateRangePicker
            startDate={draft.fromDt}
            endDate={draft.toDt}
            onChange={(start, end) =>
              setDraft((prev) => ({
                ...prev,
                fromDt: start ? formatDateForAPI(start) : '',
                toDt: end ? formatDateForAPI(end) : '',
              }))
            }
          />
        </div>
      </div>
    </ListFilterDrawer>
  )
}

export function MessageLogsPage() {
  const { hasWriteAccess } = useUserRole()
  const canOpenApplication = hasWriteAccess('application')
  const [params, setParams] = useState<MessageLogsListParams>(initialParams)
  const [searchDraft, setSearchDraft] = useState(() => params.q)
  const [rows, setRows] = useState<MessageLogRow[]>([])
  const [pageOptions, setPageOptions] = useState<
    Pick<MessageLogListResponse, 'current' | 'last' | 'total'>
  >({
    current: 1,
    last: 1,
    total: 0,
  })
  const [loading, setLoading] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)

  useEffect(() => {
    setSearchDraft(params.q)
  }, [params.q])

  const load = useCallback(async (next: MessageLogsListParams) => {
    setLoading(true)
    try {
      const res = await getMessageLogList(buildMessageLogQueryParams(next))
      setRows(res.data ?? [])
      setPageOptions({
        current: res.current ?? 1,
        last: Math.max(1, res.last ?? 1),
        total: res.total ?? 0,
      })
      writeMessageLogsSession(buildMessageLogsSessionFromParams(next))
    } catch (err) {
      showApiError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load(params)
  }, [load, params])

  useEffect(() => {
    const timer = setTimeout(() => {
      const q = searchDraft.trim()
      if (q !== params.q.trim()) {
        setParams((prev) => ({ ...prev, q, page: 1 }))
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [searchDraft, params.q])

  const sort = (orderBy: MessageLogsSortKey) => {
    setParams((prev) => ({
      ...prev,
      orderBy,
      sortBy:
        prev.orderBy === orderBy && prev.sortBy === 'desc' ? 'asc' : 'desc',
      page: 1,
    }))
  }

  const resetFilters = () => {
    setSearchDraft('')
    clearMessageLogsSession()
    setParams({ ...DEFAULT_PARAMS })
  }

  return (
    <div data-testid="message-logs-page">
      <PageShell
        title="SMS Message Logs"
        pageHeaderTestIdRoot="message-logs-page-header"
        contentClassName="!space-y-6 w-full max-w-none min-w-0"
      >
        <ListSearchToolbar
          searchValue={searchDraft}
          searchPlaceholder="Search.."
          onSearchChange={setSearchDraft}
          activeFilterCount={countActiveFilters(params)}
          onOpenFilters={() => setFiltersOpen(true)}
          testIdPrefix="message-logs"
        />

        <MessageLogsFilters
          filters={params}
          isOpen={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          onApply={(next) => setParams({ ...next, page: 1 })}
          onReset={resetFilters}
        />

        <div className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm">
          <ListPaginationBar
            placement="header"
            currentPage={pageOptions.current}
            lastPage={pageOptions.last}
            totalRecords={pageOptions.total}
            perPage={params.perPage}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            testIdPrefix="message-logs"
            summaryLayout="angular"
          />

          {loading ? (
            <div className="flex min-h-[12rem] items-center justify-center py-16">
              <Loader2
                className="h-8 w-8 animate-spin text-[rgb(21_151_255)]"
                aria-hidden
              />
            </div>
          ) : (
            <ReportTableScroll
              enabled={rows.length > 0}
              testIdPrefix="message-logs"
            >
              <table
                className={cn(
                  reportStyles.listingTable,
                  'border-collapse text-left text-slate-800',
                  '[&_thead_th]:px-2 [&_tbody_td]:px-2 sm:[&_thead_th]:px-2.5 sm:[&_tbody_td]:px-2.5',
                )}
              >
                <thead>
                  <tr>
                    {SORT_COLUMNS.map((col, index) => {
                      const active = params.orderBy === col.key
                      return (
                        <th key={`${col.key}-${index}`} className="text-left">
                          <button
                            type="button"
                            onClick={() => sort(col.key)}
                            className={cn(
                              'inline-flex items-center gap-1.5 whitespace-nowrap text-left font-semibold text-slate-600 hover:text-slate-900',
                              active && 'text-[rgb(21_151_255)]',
                            )}
                          >
                            <span>{col.label}</span>
                            <span className={cn('inline-flex items-center ml-1 text-[11px]', active ? 'text-[#1597ff]' : 'text-slate-400')}>
                              {active ? (params.sortBy === 'asc' ? '▲' : '▼') : <ChevronsUpDown className="h-3 w-3 shrink-0" aria-hidden />}
                            </span>
                          </button>
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={SORT_COLUMNS.length}
                        className="py-12 text-center text-slate-500"
                      >
                        No records found.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, index) => {
                      const applicantId = row.fk_applicant_id
                      const delivered =
                        row.error_code == null || row.error_code === ''
                      return (
                        <tr
                          key={row.id ?? `row-${index}`}
                          className="border-b border-slate-100"
                        >
                          <td>
                            {applicantId != null && canOpenApplication ? (
                              <Link
                                to={ROUTE_BUILDERS.DASHBOARD_APPLICATION_DETAIL(
                                  applicantId,
                                )}
                                className="text-[#007BFF] hover:underline"
                              >
                                {padApplicantId(applicantId)}
                              </Link>
                            ) : (
                              padApplicantId(applicantId) || '—'
                            )}
                          </td>
                          <td className="font-mono text-xs">{row.id ?? '—'}</td>
                          <td>{row.to ?? '—'}</td>
                          <td className="max-w-[28rem] whitespace-normal">
                            {row.body ?? '—'}
                          </td>
                          <td>
                            {row.delay && String(row.delay) !== '0'
                              ? `${row.delay} Sec`
                              : '—'}
                          </td>
                          <td>
                            <span
                              className={cn(
                                'rounded-full px-2 py-0.5 text-xs font-semibold',
                                delivered
                                  ? 'bg-emerald-50 text-emerald-700'
                                  : 'bg-red-50 text-red-700',
                              )}
                            >
                              {delivered ? 'Delivered' : 'Undelivered'}
                            </span>
                          </td>
                          <td>{row.error_code ?? '—'}</td>
                          <td>{row.error_message ?? '—'}</td>
                          <td>{formatDisplayDate(row.sent_at)}</td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </ReportTableScroll>
          )}

          <ListPaginationBar
            placement="footer"
            currentPage={pageOptions.current}
            lastPage={pageOptions.last}
            totalRecords={pageOptions.total}
            perPage={params.perPage}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            testIdPrefix="message-logs"
            summaryLayout="angular"
          />
        </div>
      </PageShell>
    </div>
  )
}
